<template>
  <div id="ftx-web" class="web-app">
    <!-- Topbar -->
    <header class="topbar">
      <div class="brand" @click="$router.push('/home')">
        <img v-if="platformLogo" :src="platformLogo" class="brand-logo" />
        <span class="brand-name">{{ platformName }}</span>
        <!-- 登录后显示实名信息 -->
        <template v-if="isLoggedIn && isVerified">
          <span class="brand-realname">{{ user.realName }}</span>
          <span class="brand-uid">UID: {{ user.uid }}</span>
          <span class="brand-email">实盘账号</span>
        </template>
        <!-- 未登录显示标语 -->
        <template v-else>
          <span class="brand-tagline">安全 · 专业 · 尊享</span>
        </template>
      </div>
      <nav class="topnav">
        <router-link to="/home">首页</router-link>
        <router-link to="/invite">邀请</router-link>
        <router-link to="/trade">交易</router-link>
        <router-link to="/account">我的</router-link>
      </nav>
      <div class="topbar-right">
        <template v-if="!isLoggedIn">
          <div class="guest-menu">
            <a class="auth-link-btn primary" @click="openLogin">登录</a>
            <template v-if="registerEnabled">
              <span class="sep">|</span>
              <a class="auth-link-btn" @click="openRegister">注册</a>
            </template>
            <span class="sep">|</span>
            <a class="auth-link-btn" href="#">APP</a>
            <span class="sep">|</span>
            <a class="auth-link-btn" href="#">后台</a>
          </div>
        </template>
        <template v-else>
          <div class="user-menu">
            <span class="web-uid" @click="copyUid">UID：<strong>{{ userInfo.uid }}</strong></span>
            <span class="balance">净值 <strong>{{ userInfo.equity || '0.00' }}</strong></span>
            <button class="red" @click="$router.push('/account/deposit')">充值</button>
            <button @click="handleLogout">退出</button>
          </div>
        </template>
      </div>
    </header>

    <!-- Main Content -->
    <main class="main">
      <router-view />
    </main>

    <!-- Modals -->
    <LoginModal :visible="showLogin" :register-enabled="registerEnabled" @close="showLogin=false" @switch-register="showLogin=false;showRegister=true" @switch-forgot="showLogin=false;showForgotPassword=true" @login-success="onLoginSuccess" />
    <RegisterModal v-if="registerEnabled" :visible="showRegister" @close="showRegister=false" @switch-login="showRegister=false;showLogin=true" />
    <ForgotPasswordModal :visible="showForgotPassword" @close="showForgotPassword=false" @switch-login="showForgotPassword=false;showLogin=true" />
    <GuideModal :visible="showGuide" @close="showGuide=false" @open-chat="showGuide=false;$root.$emit('open-chat')" />
    <OrderConfirmModal :visible="showOrderConfirm" :order="orderData" @close="showOrderConfirm=false" @confirmed="onOrderConfirmed" />
    <ChangeLoginPwdModal :visible="showChangeLoginPwd" @close="showChangeLoginPwd=false" />
    <ChangeTradePwdModal :visible="showChangeTradePwd" @close="showChangeTradePwd=false" />
    <NoticeDetailModal :visible="showNoticeDetail" :notice="noticeData" @close="showNoticeDetail=false" />
    <SltpModal :visible="showSltp" :position="sltpPosition" :position-count="sltpPositionCount" @close="showSltp=false" @updated="onSltpUpdated" />
    <ClosePositionModal :visible="showClosePosition" :position="closePositionData" :position-count="sltpPositionCount" :mode="closePositionMode" @close="showClosePosition=false" @closed="onClosePositionDone" />
    <HistoryDetailModal :visible="showHistoryDetail" :record="historyRecord" @close="showHistoryDetail=false" />

    <!-- Maintenance Overlay -->
    <MaintenanceOverlay :visible="isMaintenanceMode" @retry="checkMaintenance" />

    <!-- Chat Overlay -->
    <ChatOverlay :visible="showChat" @close="showChat=false" />

    <!-- Toast -->
    <div id="ftx-toast" class="toast"></div>

    <!-- Price Alert Notification -->
    <transition name="alert-slide">
      <div v-if="priceAlertVisible" class="price-alert-popup">
        <div class="alert-icon">🔔</div>
        <div class="alert-content">
          <div class="alert-title">价格预警触发</div>
          <div class="alert-msg">{{ priceAlertMsg }}</div>
        </div>
        <div class="alert-close" @click="priceAlertVisible = false">×</div>
      </div>
    </transition>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters, mapActions } from 'vuex'
import LoginModal from '@/components/LoginModal.vue'
import RegisterModal from '@/components/RegisterModal.vue'
import ForgotPasswordModal from '@/components/ForgotPasswordModal.vue'
import GuideModal from '@/components/GuideModal.vue'
import OrderConfirmModal from '@/components/OrderConfirmModal.vue'
import ChangeLoginPwdModal from '@/components/ChangeLoginPwdModal.vue'
import ChangeTradePwdModal from '@/components/ChangeTradePwdModal.vue'
import NoticeDetailModal from '@/components/NoticeDetailModal.vue'
import SltpModal from '@/components/SltpModal.vue'
import ClosePositionModal from '@/components/ClosePositionModal.vue'
import HistoryDetailModal from '@/components/HistoryDetailModal.vue'
import ChatOverlay from '@/components/ChatOverlay.vue'
import MaintenanceOverlay from '@/components/MaintenanceOverlay.vue'
import { getPublicSystemConfig } from '@/api/content'
import { syncPlatformConfigFromApi, getRegisterEnabled } from '@/config/platform'
import { getFullImageUrl, showToast } from '@/utils/common'
import { openThirdPartyKefu } from '@/utils/kefu'
import defaultLogo from '@/assets/logo/logo-icon.png'

// 价格预警功能已在客户端隐藏（App 端创建入口与本端触发弹窗均停用）；恢复时改回 true
const PRICE_ALERT_ENABLED = false

export default {
  name: 'App',
  components: { LoginModal, RegisterModal, ForgotPasswordModal, GuideModal, OrderConfirmModal, ChangeLoginPwdModal, ChangeTradePwdModal, NoticeDetailModal, SltpModal, ClosePositionModal, HistoryDetailModal, ChatOverlay, MaintenanceOverlay },
  data() {
    return {
      showLogin: false,
      showRegister: false,
      showForgotPassword: false,
      showGuide: false,
      showOrderConfirm: false,
      showChangeLoginPwd: false,
      showChangeTradePwd: false,
      showNoticeDetail: false,
      showSltp: false,
      showClosePosition: false,
      showHistoryDetail: false,
      showChat: false,
      orderData: null,
      noticeData: null,
      sltpPosition: null,
      sltpPositionCount: 0,
      closePositionData: null,
      closePositionMode: 'close',
      historyRecord: null,
      platformName: '',
      platformLogo: defaultLogo,
      platformTagline: '',
      priceAlertVisible: false,
      priceAlertMsg: ''
    }
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn', 'user', 'isMaintenanceMode']),
    ...mapGetters('trade', ['totalFloatingPnl']),
    storeShowLogin() { return this.$store.getters['user/showLoginModal'] },
    registerEnabled() { return getRegisterEnabled() },
    isVerified() {
      // 基础KYC已通过（basicKyc === '2'）且已有实名信息
      return this.$store.state.user.basicKyc === '2' && !!this.$store.state.user.realName
    },
    userInfo() {
      // 前端实时计算：usdBalance + totalFloatingPnl，随 WS 价格变动实时更新
      // 优先使用 marginLevelData.usdBalance（每 30 秒从后端刷新），降级使用 user.usdBalance
      const usdBalance = this.$store.state.trade.marginLevel.usdBalance || this.$store.state.user.usdBalance || 0
      const equity = Math.round((usdBalance + this.totalFloatingPnl) * 100) / 100
      return { ...this.user, equity }
    }
  },
  watch: {
    storeShowLogin(val) {
      if (val) {
        this.showLogin = true
        this.$store.commit('user/SET_SHOW_LOGIN', false)
      }
    }
  },
  created() {
    // Check maintenance mode first
    this.checkMaintenance()
    // 定时轮询系统配置（30秒），确保平台名称/副标题等变更即时同步
    this._configPollTimer = setInterval(() => { this.checkMaintenance() }, 30000)
    // Restore session
    if (this.isLoggedIn) {
      this.getUserInfo().catch((err) => {
        // 只有 401（认证失败）才清除 token 并退出登录
        // 其他错误（网络波动、后端暂时不可用）只静默处理，保留 token 以便下次刷新自动恢复
        const msg = (err && err.message) || ''
        if (msg.includes('401') || msg.includes('认证') || msg.includes('令牌') || msg.includes('过期')) {
          this.logout()
        } else {
          console.warn('获取用户信息失败（非认证错误，保留登录状态）:', msg)
        }
      })
    }
    // Start price updates
    this.$store.dispatch('trade/startPriceUpdates')

    // Listen for open-login event from router guard (fallback)
    this.$root.$on('open-login', () => { this.showLogin = true })
    this.$root.$on('open-guide', () => { this.showGuide = true })
    this.$root.$on('open-order-confirm', (order) => { this.orderData = order; this.showOrderConfirm = true })
    this.$root.$on('open-sltp', (pos) => {
      this.sltpPosition = pos || null
      this.sltpPositionCount = pos ? 1 : (this.$store.state.trade.positions || []).length
      this.showSltp = true
    })
    this.$root.$on('open-notice', (n) => { this.noticeData = n; this.showNoticeDetail = true })
    this.$root.$on('open-chat', () => { openThirdPartyKefu(this.user) })
    this.$root.$on('open-change-login-pwd', () => { this.showChangeLoginPwd = true })
    this.$root.$on('open-change-trade-pwd', () => { this.showChangeTradePwd = true })
    this.$root.$on('open-history-detail', (r) => { this.historyRecord = r; this.showHistoryDetail = true })
    this.$root.$on('open-close-position', (pos, mode) => {
      this.closePositionData = pos || null
      this.closePositionMode = mode || 'close'
      this.sltpPositionCount = pos ? 1 : (this.$store.state.trade.positions || []).length
      this.showClosePosition = true
    })

    // 价格预警通知（通过全局事件总线）
    this.$nextTick(() => {
      if (this.$root.constructor.prototype.$alertBus) {
        this.$root.constructor.prototype.$alertBus.$on('price-alert', this.onPriceAlert)
      }
    })
    // 兜底：延时再次绑定（确保 store 中已创建 $alertBus）
    setTimeout(() => {
      if (Vue.prototype.$alertBus) {
        Vue.prototype.$alertBus.$on('price-alert', this.onPriceAlert)
      }
    }, 2000)
  },
  beforeDestroy() {
    if (this._configPollTimer) { clearInterval(this._configPollTimer); this._configPollTimer = null }
    this.$store.dispatch('trade/stopPriceUpdates')
    this.$root.$off('open-login')
    this.$root.$off('open-guide')
    this.$root.$off('open-order-confirm')
    this.$root.$off('open-sltp')
    this.$root.$off('open-notice')
    this.$root.$off('open-chat')
    this.$root.$off('open-change-login-pwd')
    this.$root.$off('open-change-trade-pwd')
    this.$root.$off('open-history-detail')
    this.$root.$off('open-close-position')
    if (Vue.prototype.$alertBus) {
      Vue.prototype.$alertBus.$off('price-alert', this.onPriceAlert)
    }
  },
  methods: {
    ...mapActions('user', ['getUserInfo', 'logout']),
    async checkMaintenance() {
      try {
        const res = await getPublicSystemConfig()
        const data = res.data || res
        this.$store.commit('user/SET_MAINTENANCE_MODE', !!(data.maintenanceMode === 1 || data.maintenanceMode === true))
        this.$store.commit('user/SET_TRADE_ENABLED', data.tradeEnabled !== 0)
        if (data.platformName) {
          this.platformName = data.platformName
          document.title = data.platformName + ' - 交易平台'
        }
        if (data.platformSubtitle !== undefined) this.platformTagline = data.platformSubtitle
        if (data.logoUrl) this.platformLogo = getFullImageUrl(data.logoUrl)
        else this.platformLogo = defaultLogo
        // 同步平台配置到 localStorage（入金地址、网络、最低入金/出金等）
        syncPlatformConfigFromApi(data)
      } catch (e) {
        // API 调用失败时不阻断用户（可能是网络问题，不是维护模式）
        console.warn('检查维护模式失败:', e)
      }
    },
    openLogin() { this.showLogin = true; this.showRegister = false },
    openRegister() {
      if (!this.registerEnabled) return
      this.showRegister = true
      this.showLogin = false
    },
    handleLogout() {
      this.logout()
      this.$router.push('/home')
    },
    copyUid() {
      if (!this.userInfo?.uid) {
        console.warn('UID 为空，无法复制')
        return
      }
      const uidStr = String(this.userInfo.uid)
      
      // 使用 Clipboard API，带降级方案
      if (navigator.clipboard) {
        navigator.clipboard.writeText(uidStr).then(() => {
          showToast('UID 已复制到剪贴板')
        }).catch(err => {
          console.error('复制 UID 失败:', err)
          showToast('复制失败，请手动复制')
        })
      } else {
        // 降级方案
        try {
          const textArea = document.createElement('textarea')
          textArea.value = uidStr
          textArea.style.position = 'fixed'
          textArea.style.opacity = '0'
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
          showToast('UID 已复制到剪贴板')
        } catch (err) {
          console.error('降级复制失败:', err)
          showToast('复制失败，请手动复制')
        }
      }
    },
    onLoginSuccess() {
      this.$store.dispatch('user/getUserInfo').then(() => {
        // 登录成功后导航到之前想去的页面
        const redirect = this.$store.getters['user/redirectPath']
        if (redirect) {
          this.$store.commit('user/SET_REDIRECT_PATH', '')
          this.$router.push(redirect)
        } else {
          this.$router.push('/home')
        }
      })
    },
    onOrderConfirmed() {
      this.$store.dispatch('trade/fetchPositions')
    },
    onSltpUpdated() {
      this.$store.dispatch('trade/fetchPositions')
    },
    onClosePositionDone() {
      this.$store.dispatch('trade/fetchPositions')
      this.$store.dispatch('trade/fetchPendingOrders')
    },
    onPriceAlert(data) {
      if (!PRICE_ALERT_ENABLED) return
      // 只展示属于当前用户的预警
      const myUid = this.$store.state.user.uid
      if (data.uid && myUid && data.uid !== myUid) return
      this.priceAlertMsg = data.message || `${data.symbol} 价格预警已触发`
      this.priceAlertVisible = true
      // 5 秒后自动关闭
      if (this._alertTimer) clearTimeout(this._alertTimer)
      this._alertTimer = setTimeout(() => { this.priceAlertVisible = false }, 5000)
    }
  }
}
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@/styles/_common.scss';

/* Price Alert Popup */
.price-alert-popup {
  position: fixed;
  top: 80px;
  right: 24px;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid rgba(255, 204, 0, 0.3);
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 204, 0, 0.15);
  min-width: 280px;
  max-width: 400px;
  cursor: default;
}
.alert-icon { font-size: 24px; flex-shrink: 0; animation: alert-bounce 0.5s ease; }
.alert-content { flex: 1; min-width: 0; }
.alert-title { font-size: 14px; font-weight: 700; color: #ffcc00; margin-bottom: 2px; }
.alert-msg { font-size: 13px; color: #e0e0e0; }
.alert-close {
  flex-shrink: 0;
  font-size: 20px;
  color: #888;
  cursor: pointer;
  line-height: 1;
  &:hover { color: #fff; }
}
.alert-slide-enter-active { transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.alert-slide-leave-active { transition: all 0.3s ease; }
.alert-slide-enter { transform: translateX(400px); opacity: 0; }
.alert-slide-leave-to { transform: translateX(400px); opacity: 0; }
@keyframes alert-bounce {
  0% { transform: scale(0); }
  50% { transform: scale(1.3); }
  100% { transform: scale(1); }
}
</style>
